/* eslint-disable no-unused-vars,@typescript-eslint/no-unused-vars */
import { MouseEventDelegate } from './interface/mouse-event-delegate';
import { ISlideModel } from '@src/slide/domain/ui-model/element-modeles/Interface/slide-model.interface';
import { transaction } from 'mobx';
import changeFocusToCanvas from '@com/pc-left-thumbnail-list/utils';

/**
 * 鼠标事件处理，包括各种点击，enter，leave
 */
export default class MouseEventHandler {
    private mouseDelegate: MouseEventDelegate;
    private lastClickIndex = 0;
    private forMouseUp;
    private datumIndex: number;

    public constructor(mouseDelegate: MouseEventDelegate) {
        this.mouseDelegate = mouseDelegate;
    }

    /**
     * 处理鼠标事件
     * @param ev
     * @param listIndex
     * @param model 当前操作的model
     */
    public handleMouseDown(ev, listIndex: number, clickSlide: ISlideModel) {
        const { selectionService } = this.mouseDelegate.getServices();
        const { currentSlide, currentSlideIndex = 0 } = selectionService || { };
        const { lastClickIndex } = this;// 上次点击位置

        // 处理添加新slide情况
        let datumPoint = this.getDatumPoint();
        const clickDaum = selectionService?.selectedSlides.indexOfObject(clickSlide); // 鼠标点击slide在选中列表里的index

        // 清除this.forMouseUp，避免拖拽后再次点击时选中态错乱
        this.forMouseUp = null;

        if (this.isCtrlKey(ev)) { // control
            datumPoint = listIndex;// control修改基准点
            this.handleCtrlClick(currentSlideIndex, clickSlide, listIndex);
        } else if (ev.shiftKey) { // shift
            this.handleShiftClick(lastClickIndex, datumPoint, listIndex);
        } else if (ev.button === 2) { // 右键
            const { selectedArray = [] } = selectionService?.selectedSlides || { };
            const selectedArrayLength = selectedArray.length;
            if (selectedArrayLength > 1) { // 多选
                if (clickDaum === -1) { // 右键多选以外的内容
                    datumPoint = listIndex;
                    this.handleCustomClick(currentSlideIndex, listIndex, clickSlide);
                }
            } else if (currentSlideIndex !== listIndex || currentSlide?.type !== clickSlide.type) { // 右键非ui选中文件
                datumPoint = listIndex;
                this.handleCustomClick(currentSlideIndex, listIndex, clickSlide);
            } else { // 右键文件
                this.handleRightMouseClick(clickSlide, selectedArrayLength);
                return;
            }
        } else { // custom click 左键
            this.mouseDelegate.getServices().menuManager?.hideAllMenu();
            datumPoint = listIndex;
            this.handleLeftClick(listIndex, clickSlide);
        }
        this.mouseDelegate.clearClearanceSelectIndex();
        this.mouseDelegate.clearClearance();

        this.datumIndex = datumPoint; // 基准点，shift不变。其余都变
        this.lastClickIndex = listIndex; // 上次点击的index

        document.addEventListener('mouseup', this.handleMouseUp); // frame监听
    }

    public handleLeftClick(index, slide) {
        const { selectionService } = this.mouseDelegate.getServices();
        const { currentSlideIndex = 0 } = selectionService || { };// 右边正在展示的slide index
        const selectedArray1 = selectionService?.selectedSlides.selectedArray ?? [];
        if (selectedArray1.indexOf(slide) === -1) {
            this.handleCustomClick(currentSlideIndex, index, slide);
        } else {
            this.forMouseUp = {
                currentSlideIndex,
                index,
                slide,
            };
        }
        changeFocusToCanvas();
    }

    public clearForMouseUp() {
        this.forMouseUp = null;
    }

    private getDatumPoint() {
        const { selectionService } = this.mouseDelegate.getServices();
        let datumPoint = this.datumIndex === undefined ? 0 : this.datumIndex;// 基准点
        const datumSlide = this.mouseDelegate.getCurList[datumPoint];// 基准点对应的slide
        const lastDaum = selectionService?.selectedSlides.indexOfObject(datumSlide);// 基准slide在选中列表里的index
        if (lastDaum === -1) { // 基准slide未被选中时，将当前
            datumPoint = selectionService?.currentSlideIndex ?? 0;
        }
        return datumPoint;
    }

    private isCtrlKey(ev) {
        return ev.metaKey || ev.ctrlKey;
    }

    private handleCtrlClick = (currentSlideIndex, slide, index) => {
        const { uiDataService, selectionService } = this.mouseDelegate.getServices();
        if (slide.selected) {
            if (!this.selectedSlidesFromStore) return;
            if (this.selectedSlidesFromStore.length <= 1) return;
            this.selectedSlidesFromStore.remove(slide);
            if (currentSlideIndex === index) { // 取消选slide为current时从select栈里拿最后一个入栈元素设为current
                const lastAddSlideIndex = this.selectedSlidesFromStore.length - 1;
                const lastAddSlide = this.selectedSlidesFromStore.objectAtIndex(lastAddSlideIndex);
                const newIndex = uiDataService?.presentation.indexOfSlide(lastAddSlide) ?? 0;
                if (currentSlideIndex !== newIndex) {
                    selectionService?.changeCurrentSlideIndexByUserAction(newIndex);
                }
            }
        } else {
            // 在母版编辑状态，可能出现 SlideType 不同的情况
            const { currentSlideType } = selectionService || { };
            if (currentSlideIndex !== index || currentSlideType !== slide.type) {
                selectionService!.currentSlideIndex = index;
                selectionService?.updateCurrentSlideType(slide.type);
            }
            this.selectedSlidesFromStore?.add(slide);
        }
    };

    private handleShiftClick = (lastClickIndex, datumPoint, index) => {
        const { uiDataService, selectionService } = this.mouseDelegate.getServices();
        if (lastClickIndex !== datumPoint) { // 上次操作也是shift 需要先pop掉
            let lastStart = lastClickIndex;
            let lastEnd = lastClickIndex;
            if (lastClickIndex > datumPoint) {
                lastStart = datumPoint;
            } else {
                lastEnd = datumPoint;
            }
            for (let i = lastStart; i <= lastEnd; i++) {
                selectionService?.selectedSlides.remove(uiDataService?.presentation.slideOfIndex(i));
            }
        }
        let start = datumPoint;
        let end = datumPoint;
        let sequence = 1;// 1 顺序 2 逆序
        if (datumPoint > index) {
            start = index;
            sequence = 2;
        } else {
            end = index;
            sequence = 1;
        }
        transaction(() => {
            if (sequence === 1) { // 按序入栈
                for (let i = start; i <= end; i++) {
                    selectionService?.selectedSlides.add(uiDataService?.presentation.slideOfIndex(i));
                }
            } else {
                for (let i = end; i >= start; i--) {
                    selectionService?.selectedSlides.add(uiDataService?.presentation.slideOfIndex(i));
                }
            }
        });
    };

    private handleRightMouseClick = (slide, selectedArrayLength) => {
        if (selectedArrayLength === 0) {
            const { selectionService } = this.mouseDelegate.getServices();
            selectionService?.selectedSlides.clearAndAdd(slide);
        }
    };

    private handleCustomClick = (currentSlideIndex, index, slide) => {
        transaction(() => {
            const { selectionService, selectedAnims } = this.mouseDelegate.getServices();
            if (currentSlideIndex !== index) {
                selectionService?.changeCurrentSlideIndexByUserAction(index);
            }
            selectionService?.selectedElements.clear();
            selectedAnims?.clear();
            selectionService?.selectedSlides.clearAndAdd(slide);
            selectionService?.updateCurrentSlideType(slide.type);
        });
    };

    private handleMouseUp = () => {
        if (this.forMouseUp) {
            const {
                currentSlideIndex,
                index,
                slide,
            } = this.forMouseUp;
            this.handleCustomClick(currentSlideIndex, index, slide);
            this.forMouseUp = null;
        }
        document.removeEventListener('mouseup', this.handleMouseUp);
        // 点击iframe时，将焦点改到canvas中
        changeFocusToCanvas();
    };

    private get selectedSlidesFromStore() {
        return this.mouseDelegate.getServices().selectionService?.selectedSlides;
    }
}
